import { useToast } from '@chakra-ui/react';
import { IApiClientException } from 'types/api';
import { ApiClientException } from 'utils/apiClientException';

type HTTPInit = Omit<RequestInit, 'method' | 'body'>;

export abstract class HTTP {
  static getDefaultHeaders() {
    return new Headers({});
  }

  private static getInput(endpoint: string) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL ?? `${window.location.origin}/wp-json/mindbody/v1`;

    if (endpoint.charAt(0) !== '/') endpoint = '/' + endpoint;

    return baseUrl + endpoint;
  }

  public static get<T>(endpoint: string, params = {}, init?: HTTPInit) {
    const queryparams = new URLSearchParams(params).toString();
    if (queryparams) endpoint += `?${queryparams}`;

    return HTTP.request<T>(HTTP.getInput(endpoint), { method: 'GET', ...init });
  }

  public static post<T>(endpoint: string, body = {}, init?: HTTPInit) {
    return HTTP.request<T>(HTTP.getInput(endpoint), {
      method: 'POST',
      ...init,
      body: JSON.stringify(body),
    });
  }

  public static patch<T>(endpoint: string, body = {}, init?: HTTPInit) {
    return HTTP.request<T>(HTTP.getInput(endpoint), {
      method: 'PATCH',
      ...init,
      body: JSON.stringify(body),
    });
  }

  public static put<T>(endpoint: string, body = {}, init?: HTTPInit) {
    return HTTP.request<T>(HTTP.getInput(endpoint), {
      method: 'PUT',
      ...init,
      body: JSON.stringify(body),
    });
  }

  public static delete<T>(endpoint: string) {
    return HTTP.request<T>(HTTP.getInput(endpoint), { method: 'DELETE' });
  }

  private static request<T>(input: RequestInfo, init: RequestInit) {
    return new HTTPRequest<T>(input, init, HTTP.getDefaultHeaders());
  }
}

class HTTPRequest<T> {
  constructor(
    private readonly input: RequestInfo,
    private readonly init: RequestInit,
    private readonly headers = new Headers({}),
  ) {}

  private abortController = new AbortController();

  async send(): Promise<T> {
    return fetch(this.input, {
      ...this.init,
      credentials: 'include',
      headers: this.headers,
      signal: this.abortController.signal,
    }).then((response) => {
      if (response.ok) return response.json() as Promise<T>;
      return response.json().then((data: IApiClientException) => {
        throw new ApiClientException(data.Message, data.Code, data.ErrorCode);
      });
    });
  }

  abort() {
    this.abortController.abort();
  }

  setHeader(name: string, value: string) {
    this.headers.set(name, value);
    return this;
  }

  unsetHeader(name: string) {
    this.headers.delete(name);
    return this;
  }
}

export function useCopyToClipboard() {
  const toast = useToast();

  const copyToClipboard = (element?: HTMLInputElement, successMessage?: string) => {
    if (element) element.select();

    if (!navigator.clipboard) {
      document.execCommand('copy');
    } else {
      const text = window.getSelection()?.toString();
      if (text) {
        navigator.clipboard.writeText(text);
      }
    }

    document.getSelection()?.removeAllRanges();

    toast({ title: successMessage, status: 'success' });
  };

  return copyToClipboard;
}
